import { useMemo } from 'react'

import { get, keyBy } from 'lodash'
import { useSelector } from 'react-redux'

import { selectSelectivityLabels } from 'store/user'

export const MOST = 'Most'
export const HIGHLY = 'Highly'
export const VERY = 'Very'
export const SELECTIVE = 'Selective'
export const SOMEWHAT = 'Somewhat'
export const OPEN = 'Open'
export const MOST_IDX = 0
export const HIGHLY_IDX = 1
export const VERY_IDX = 2
export const SELECTIVE_IDX = 3
export const SOMEWHAT_IDX = 4
export const OPEN_IDX = 5

const DEFAULT_LABEL = {
  [MOST]: 'Most Selective',
  [HIGHLY]: 'Highly Selective',
  [VERY]: 'Very Selective',
  [SELECTIVE]: 'Selective',
  [SOMEWHAT]: 'Somewhat Selective',
  [OPEN]: 'Open Admission',
} as const

export const HS_LABEL_FIELD_NAME = {
  [MOST]: 'SelectivityMostSelectiveLabel',
  [HIGHLY]: 'SelectivityHighlySelectiveLabel',
  [VERY]: 'SelectivityVerySelectiveLabel',
  [SELECTIVE]: 'SelectivitySelectiveLabel',
  [SOMEWHAT]: 'SelectivitySomewhatSelectiveLabel',
  [OPEN]: 'SelectivityOpenAdmissionLabel',
}

export const getSelectivityLabelFromHS = (hs: any, name: string): string => {
  return get(hs, get(HS_LABEL_FIELD_NAME, name), get(DEFAULT_LABEL, name))
}

export const EMPTY_LABEL = 'No Selectivity Specified'

const configs = [
  {
    label: DEFAULT_LABEL[MOST],
    fieldLabel: DEFAULT_LABEL[MOST],
    fieldName: HS_LABEL_FIELD_NAME[MOST],
    name: MOST,
    legacyRange: [83.35, 100],
    value: 6,
  },
  {
    label: DEFAULT_LABEL[HIGHLY],
    fieldLabel: DEFAULT_LABEL[HIGHLY],
    fieldName: HS_LABEL_FIELD_NAME[HIGHLY],
    name: HIGHLY,
    legacyRange: [66.68, 83.35],
    value: 5,
  },
  {
    label: DEFAULT_LABEL[VERY],
    fieldLabel: DEFAULT_LABEL[VERY],
    fieldName: HS_LABEL_FIELD_NAME[VERY],
    name: VERY,
    legacyRange: [50.01, 66.68],
    value: 4,
  },
  {
    label: DEFAULT_LABEL[SELECTIVE],
    fieldLabel: DEFAULT_LABEL[SELECTIVE],
    fieldName: HS_LABEL_FIELD_NAME[SELECTIVE],
    name: SELECTIVE,
    legacyRange: [33.34, 50.01],
    value: 3,
  },
  {
    label: DEFAULT_LABEL[SOMEWHAT],
    fieldLabel: DEFAULT_LABEL[SOMEWHAT],
    fieldName: HS_LABEL_FIELD_NAME[SOMEWHAT],
    name: SOMEWHAT,
    legacyRange: [16.67, 33.34],
    value: 2,
  },
  {
    label: DEFAULT_LABEL[OPEN],
    fieldLabel: DEFAULT_LABEL[OPEN],
    fieldName: HS_LABEL_FIELD_NAME[OPEN],
    name: OPEN,
    legacyRange: [0, 16.67],
    value: 1,
  },
]

export interface IConfig {
  label: string
  fieldName: string
  fieldLabel: string
  value: number
}

export const useSelectivityConfigs = () => {
  const selectivityLabelOverrides = useSelector(selectSelectivityLabels)

  const selectivityConfigs = useMemo(() => {
    const updated: IConfig[] = []

    configs.forEach((config) => {
      updated.push({
        ...config,
        label: get(selectivityLabelOverrides, config.name, config.label),
      })
    })

    return updated
  }, [selectivityLabelOverrides])

  return selectivityConfigs
}

export const useSelectivityLabel = (selectivity: number) => {
  const selectivityLabelConfigs = useSelectivityConfigs()
  const configsByValue = keyBy(selectivityLabelConfigs, 'value')
  return configsByValue[selectivity]?.label
}

export type ILabelsByValue = Record<number, string | undefined>

export const useSelectivityLabels = () => {
  const selectivityLabelOverrides = useSelector(selectSelectivityLabels)
  const labels = useMemo(() => {
    const updated: ILabelsByValue = {
      0: EMPTY_LABEL,
    }
    configs.forEach((config) => {
      updated[config.value] = get(selectivityLabelOverrides, config.name, config.label)
    })
    return updated
  }, [selectivityLabelOverrides])

  return labels
}
